<template>
  <div
    :class="[
      'mb-20 grid grid-cols-1 gap-16 md:grid-cols-3 lg:grid-cols-4',
      isWide && 'xl:grid-cols-5',
    ]"
  >
    <SwapBlockListing v-if="isBuybackEnabled" class="hidden h-full lg:block" />
    <div
      v-for="(product, index) in productsWithIphone17"
      :key="getItemKey(product)"
    >
      <Iphone17ProductCard v-if="product.id === 'iPhone17'" :product />
      <ProductCard
        v-else
        :id="product.id"
        :brand="product.brandClean"
        :category="product.category"
        :category-id="product.categoryId"
        :color-swatches="product.specifications?.color ?? []"
        :comparison-products
        :deals="product.deals"
        :description="product.description"
        :discounted-price="product.discountedPrice"
        :image-alt="product.title"
        :image-src="product.image"
        :is-comparison-add-product-disabled
        :is-comparison-enabled
        :is-responsive-comparison-enabled
        :listing-id="product.listingId"
        :listing-public-id="product.listingPublicId ?? ''"
        :marketing-tags="product.marketingTags"
        :model="product.modelClean"
        :price="product.price"
        :price-new="product.priceNew"
        :product
        :review-rating="product.reviewRating"
        :scores="product.scores"
        :special-offer-type="product.specialOfferType"
        :specifications="
          product.specifications
            ? Object.entries(product.specifications)
                .map(([key, values]) => ({
                  label: key,
                  values: values.map(
                    ({ name, value }) => name + (value ? ` (${value})` : ''),
                  ),
                }))
                .filter(({ label }) => !excludedSpecifications.has(label))
            : []
        "
        :title="product.title"
        :url="product.productPageLink"
        :with-starting-from="product.granularity === 'parent'"
        @link-click="handleProductClick(product, index)"
        @toggle-comparison-product="emit('toggle-comparison-product', $event)"
      />
    </div>

    <div
      v-if="$slots.cmsSection"
      :class="[
        'rounded-lg overflow-hidden md:col-span-3 lg:col-span-4',
        isWide && 'xl:col-span-5',
        { 'row-start-4 lg:row-start-2': products.length >= 2 },
      ]"
    >
      <slot name="cmsSection" />
    </div>

    <div
      v-if="
        !hideBanner &&
        !$slots.cmsSection &&
        marketingContent?.srcLarge &&
        marketingContent?.srcSmall &&
        marketingContent?.label
      "
      :ref="
        setupBannerImpressionTracker({
          name: 'landing_page',
          creative: marketingContent.label,
          position: 'landing_page_3',
        })
      "
      :class="[
        'md:col-span-3 lg:col-span-4',
        isWide && 'xl:col-span-5',
        { 'row-start-4 lg:row-start-2': products.length >= 2 },
      ]"
    >
      <MarketingCard
        :alt="marketingContent.alt"
        :label="marketingContent.label"
        :srcLarge="marketingContent.srcLarge"
        :srcSmall="marketingContent.srcSmall"
        :url="marketingContent.url"
        @click="
          trackLandingBannerClick({
            name: 'landing_page',
            creative: marketingContent.label,
            position: 'landing_page_3',
          })
        "
      />
    </div>
    <div
      v-if="article"
      :class="[
        'md:col-span-3 lg:col-span-4',
        isWide && 'xl:col-span-5',
        { 'row-start-10 md:row-start-8 lg:row-start-5': products.length >= 7 },
      ]"
    >
      <ArticleCard
        :id="article.id"
        :category="article.category"
        :imageAlt="article.imageAlt"
        :imageSrc="article.imageSrc"
        :link="article.link"
        :text="article.text"
        :title="article.title"
        variant="full"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useRoute, useRuntimeConfig, useState } from '#imports'
import { computed, toRef } from 'vue'

import { useBuybackEnabled } from '@backmarket/nuxt-layer-buyback/app/composables/useBuybackEnabled/useBuybackEnabled'
import { useDiscountedPrice } from '@backmarket/nuxt-layer-buyback/app/scopes/offer/composables/useBuybackOffer/useDiscountedPrice'
import type { ArticleCard as ArticleCardType } from '@backmarket/nuxt-layer-cms/ArticleCard/ArticleCard.types'
import ArticleCard from '@backmarket/nuxt-layer-cms/ArticleCard/ArticleCard.vue'
import ProductCard from '@backmarket/nuxt-layer-recommendation/ProductCard.vue'
import type {
  ComparisonProduct,
  Product,
} from '@backmarket/nuxt-layer-recommendation/models/product'
import { useClickTracking } from '@backmarket/nuxt-layer-recommendation/useProductTracking'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'

import SwapBlockListing from '~/scopes/buyback/swap/components/ListingSwapOffer/ListingSwapOffer.vue'

import { useImpressionTracker } from '../../composables/useImpressionTracker'
import type { SearchTrackingData } from '../../composables/useProductsSearch/useProductsSearch.types'

import Iphone17ProductCard from './components/Iphone17ProductCard/Iphone17ProductCard.vue'
import MarketingCard from './components/MarketingCard/MarketingCard.vue'

const { trackProductClick } = useClickTracking()

const { trackLandingBannerImpression, trackLandingBannerClick } = useTracking()
const { setupImpressionTracker: setupBannerImpressionTracker } =
  useImpressionTracker(trackLandingBannerImpression)

const searchTrackingData = useState<SearchTrackingData>('search-query')
const emit = defineEmits(['toggle-comparison-product'])

const props = defineProps<{
  products: Product[]
  marketingContent?: {
    alt?: string
    label?: string
    srcSmall?: string
    srcLarge?: string
    url?: string
  }
  pageInput?: number
  article?: ArticleCardType
  reviewsCount?: number
  reviewsRate?: number
  comparisonProducts?: ComparisonProduct[]
  isComparisonEnabled?: boolean
  isComparisonAddProductDisabled?: boolean
  isResponsiveComparisonEnabled?: boolean
  isFiltered?: boolean
  withIphone17?: boolean
  isWide?: boolean
}>()

const { products } = useDiscountedPrice(toRef(props, 'products'))
const { countryCode } = useMarketplace().market
const { FF_PRODUCT_PAGE_IPHONE_17_EVENT_PAGE = '', FF_PLP_IPHONE17_UUID = '' } =
  useRuntimeConfig().public
const route = useRoute()
const { isBuybackEnabled } = useBuybackEnabled()

const experiments = useExperiments()
const hideBanner = experiments['experiment.hidePlpBanner'] === 'with'

// To have a good accessibility position
const productsWithIphone17 = computed(() => {
  return products.value.reduce(
    (acc, product, index) => {
      // FF_PRODUCT_PAGE_IPHONE_17_EVENT_PAGE needs to be opened for the country
      // The actual landing page needs to be part of FF_PLP_IPHONE17_UUID
      // We only want to display the iPhone 17 on the first page
      // We only want to display the iPhone 17 on non filtered pages
      // We want to display the iPhone 17 at the 3rd and 17th places
      if (
        FF_PRODUCT_PAGE_IPHONE_17_EVENT_PAGE.includes(countryCode) &&
        FF_PLP_IPHONE17_UUID.includes(route.params.uuid as string) &&
        props.pageInput === 1 &&
        !props.isFiltered &&
        props.withIphone17 &&
        (index === 2 || index === 16)
      ) {
        acc.push({
          id: 'iPhone17',
          position: index,
        } as unknown as Product & { position?: number })
      }

      acc.push(product)

      return acc
    },
    [] as Array<Product & { position?: number }>,
  )
})

const excludedSpecifications = new Set(['color', 'storage'])

function getItemKey(item: Product & { position?: number }): string {
  if (item.id === 'iPhone17') return String(item.position)

  return item.listingId
}

function handleProductClick(product: Product, index: number) {
  trackProductClick(product, {
    provider: 'algolia',
    list: 'landing page product',
    position: index + 1,
    source: 'search',
    queryID: searchTrackingData.value?.id,
    index: searchTrackingData.value?.index,
  })
  searchTrackingData.value.productObjectId = product.objectId
}
</script>
